import { useSorted } from "@vueuse/core";
import { computed, toValue } from "vue";

import { createRowComparator } from "#ce/components/SpTable/sorting.js";

/**
 * Owns the sort state a table renders and, when `clientSort` is set, the reordering itself.
 *
 * The affordance and `aria-sort` are rendered from this state whether or not the table reorders, so
 * a server-sorted table reports its order through the same props.
 *
 * @param {Object} options
 * @param {import("vue").MaybeRefOrGetter<Array>} options.columns
 * @param {import("vue").MaybeRefOrGetter<Array>} options.items
 * @param {import("vue").MaybeRefOrGetter<String>} options.sort - "field:direction"
 * @param {import("vue").MaybeRefOrGetter<Boolean>} options.clientSort
 * @param {import("vue").MaybeRefOrGetter<Function>} [options.sortFn] - sorting strategy `(items, compare) => items`
 * @returns {Object} `{ field, direction, sortedItems, nextSortFor }`
 */
export function useTableSort({ columns, items, sort, clientSort, sortFn }) {
  const field = computed(() => parse(toValue(sort)).field);
  const direction = computed(() => parse(toValue(sort)).direction);

  const sortedColumn = computed(() => toValue(columns).find((column) => column.field === field.value));

  const comparator = computed(() => {
    if (!sortedColumn.value) {
      return () => 0;
    }

    return createRowComparator(sortedColumn.value, direction.value);
  });

  // useSorted copies before sorting, so `items` is never mutated. Both functions read their source
  // when they run rather than when they are built, which is what keeps the result tracking the sort
  // state and a `sortFn` replaced after mount.
  const reordered = useSorted(
    () => toValue(items),
    (left, right) => comparator.value(left, right),
    {
      sortFn: (copy, compare) => {
        const strategy = toValue(sortFn);

        return strategy ? strategy(copy, compare) : copy.sort(compare);
      },
    },
  );

  const sortedItems = computed(() => {
    if (!toValue(clientSort) || !sortedColumn.value) {
      return toValue(items);
    }

    return reordered.value;
  });

  // A fresh column starts descending, the direction a reader wants first for dates and counts; the
  // sorted column toggles.
  function nextSortFor(column) {
    const next = field.value === column.field && direction.value === "desc" ? "asc" : "desc";

    return { field: column.field, direction: next };
  }

  return { field, direction, sortedItems, nextSortFor };
}

function parse(value) {
  const [field, direction] = (value ?? "").split(":");

  return { field: field || undefined, direction: direction === "asc" ? "asc" : "desc" };
}
